<template>
  <Page :title="item ? 'Rechnung ' + item.number : 'Rechnung'" color="info">
    <template slot="help">
      Hier siehst du Details zu deiner Kursrechnung und kannst diese Bearbeiten,
      Löschen oder Ausdrucken.
    </template>
    <template slot="extension">
      <v-tabs background-color="info darken-1" hide-slider>
        <v-toolbar color="info darken-1" dense>
          <v-btn text exact :to="{ name: page }"
            ><v-icon left>mdi-arrow-left</v-icon>zurück</v-btn
          >
        </v-toolbar>
      </v-tabs>
    </template>

    <v-card v-if="item">
      <v-system-bar> Rechnung {{ item.number }} </v-system-bar>
      <v-card-text>
        <v-row v-if="item">
          <v-col
            cols="12"
            md="5"
            class="full-height d-flex flex-column justify-space-between"
          >
            <FinanceInvoice
              :item="item"
              :showGroup="showGroup"
            ></FinanceInvoice>
          </v-col>
          <v-col cols="12" md="7">
            <FinanceInvoiceDetail :item="item"></FinanceInvoiceDetail>
          </v-col>
        </v-row>

        <v-row v-if="showGroup">
          <v-col cols="12">
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  Sammelrechnung {{ item.group.number }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <InvoiceTable
                    :items="groupItem.invoices"
                    :loading="loading"
                    @showDetails="$emit('showDetails', $event)"
                    :group="false"
                  >
                  </InvoiceTable>
                  <v-list>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-subtitle
                          >Beschreibung</v-list-item-subtitle
                        >
                        <v-list-item-title>
                          {{ groupItem.description }}
                        </v-list-item-title>
                      </v-list-item-content>
                      <CurrencyValue :value="total" />
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions v-if="canDelete || canChange || canDistribute">
        <v-btn text color="danger" v-if="canDelete" @click="trash()"
          >Löschen</v-btn
        >
        <v-btn
          text
          color="info"
          v-if="canChange"
          :to="{ name: 'InvoiceEdit', params: { id: item.id } }"
          >Bearbeiten</v-btn
        >
        <v-btn
          text
          color="info"
          v-if="canDistribute"
          :to="{ name: 'InvoiceDistribute', params: { id: item.id } }"
          >Betrag verteilen</v-btn
        >

        <v-spacer />
        <v-btn color="success" v-if="canChange" text @click="submit()"
          >Einreichen</v-btn
        >

        <v-btn
          text
          color="success"
          v-if="canDistribute"
          :disabled="item.details.length == 0"
          @click="enter()"
          >verbuchen</v-btn
        >
      </v-card-actions>
    </v-card>

    <v-dialog width="500" v-model="printDialog" v-if="item">
      <v-card>
        <v-card-title>Weiteres Vorgehen</v-card-title>
        <v-card-text>
          <ul>
            <li>Rechnung herunterladen, ausdrucken und unterschreiben.</li>
            <li>
              unterschriebene Rechnung
              <span v-if="item.account.code != 'byod'">mit Originalbeleg</span>
              (einseitig, ohne Bostitch) auf der Kanzlei abgeben.
            </li>
          </ul>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <ReportButton
            resource="report/invoice"
            :parameters="{ id: item.id }"
            text
            color="danger"
            >Rechnung</ReportButton
          >

          <v-spacer />
          <v-btn text @click="printDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </Page>
</template>

<script>
import { defineComponent } from "vue";
import CurrencyValue from "common/components/CurrencyValue.vue";
import FinanceInvoice from "common/components/Finance/FinanceInvoice.vue";
import FinanceInvoiceDetail from "common/components/Finance/FinanceInvoiceDetail.vue";
import ReportButton from "@/components/ReportButton.vue";
import InvoiceTable from "./Components/InvoiceTable.vue";
import { compareStudentsBySchoolClass } from "common/utils/people.js";
export default defineComponent({
  components: {
    CurrencyValue,
    FinanceInvoice,
    FinanceInvoiceDetail,
    ReportButton,
    InvoiceTable,
  },
  props: {
    id: {},
    page: { type: String, default: "InvoicePending" },
  },
  data() {
    return {
      loading: false,
      item: null,
      groupItem: null,
      search: "",
      printDialog: false,
    };
  },
  computed: {
    canChange() {
      if (this.item.account.code == "byod") {
        return false;
      }
      return (
        this.item.status.code == "created" ||
        this.item.status.code == "rejected"
      );
    },
    canDelete() {
      return (
        this.item.status.code == "created" ||
        this.item.status.code == "rejected"
      );
    },

    canDistribute() {
      return this.item.status.code == "noDetails";
    },
    canEnter() {
      return (
        this.item.status.code == "noDetails" && this.item.details.length > 0
      );
    },
    showGroup() {
      return (
        this.item && this.groupItem && this.item.payee.code !== "schoolFund"
      );
    },
    total() {
      if (!this.groupItem || !this.groupItem.invoices) return 0;
      const total = this.groupItem.invoices.reduce(
        (sum, invoice) => sum + invoice.amount,
        0
      );
      return total;
    },
  },
  watch: {
    id() {
      this.fetchData();
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.item = await this.apiGet({
        resource: "finance/invoice",
        id: this.id,
      });

      this.item.details.sort((a, b) =>
        compareStudentsBySchoolClass(a.person, b.person)
      );
      if (this.item.status.code == "submitted") {
        this.printDialog = true;
      }
      if (
        !this.item.department &&
        this.item.group &&
        this.item.payee.code !== "schoolFund"
      ) {
        this.groupItem = await this.apiGet({
          resource: "finance/invoicegroup",
          id: this.item.group.id,
        });
      }
      this.loading = false;
    },

    async enter() {
      if (
        await this.$root.confirm({
          message: "Soll diese Kursrechnung verbucht werden?",
          color: "success",
          icon: "mdi-send",
        })
      ) {
        await this.apiPatch({
          resource: "finance/invoice",
          id: this.item.id,
          key: "action",
          value: "enter",
        });
        this.$emit("update");
        this.fetchData();
      }
    },
    async submit() {
      if (
        await this.$root.confirm({
          message: "Soll diese Kursrechnung eingereicht werden?",
          color: "success",
          icon: "mdi-send",
        })
      ) {
        await this.apiPatch({
          resource: "finance/invoice",
          id: this.item.id,
          key: "action",
          value: "submit",
        });
        this.$emit("update");
        this.fetchData();
      }
    },
    async trash() {
      if (
        await this.$root.confirm({
          message: "Soll diese Kursrechnung gelöscht werden?",
          color: "danger",
          icon: "mdi-trash-can",
        })
      ) {
        await this.apiDelete({
          resource: "finance/invoice",
          id: this.item.id,
        });
        this.$emit("update");
        this.$router.push({ name: this.page });
      }
    },
  },
  async created() {
    this.fetchData();
  },
});
</script>
